import { useCallback, useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin';
import { MotionPathPlugin } from 'gsap/dist/MotionPathPlugin';

import { observeElementResize } from '#/lib/observe-element-resize';
import { COLORS } from '#/lib/utils';

import { TranscendLogo } from '../TranscendLogo';
import { AnimatedBorderGenerator } from './AnimationAtoms/AnimatedPathGenerator';
import { AvatarBlock } from './AnimationAtoms/AvatarBlock';
import { CardIconBlock } from './AnimationAtoms/CardIconBlock';
import { InitiativesBlock } from './AnimationAtoms/InitiativesBlock';
import {
  addBorderAnimation,
  iconStackDurations,
  LINE_DRAW_SPEED,
} from './animationUtils';
import { cardIconBlocks } from './content';
import { usePauseTimelineWhenOffscreen } from './usePauseTimelineWhenOffscreen';

gsap.registerPlugin(DrawSVGPlugin, MotionPathPlugin);

export function MobileAnimation() {
  const timelineRef = useRef<GSAPTimeline>(null);
  const container = useRef<HTMLDivElement>(null);
  const [activeButton, setActiveButton] = useState(0);

  // Pause DrawSVG work while offscreen so nav/CTA clicks aren't competing
  // with an infinite GSAP loop on the main thread.
  const isVisibleRef = usePauseTimelineWhenOffscreen(container, timelineRef);

  // Function to restart the animation
  const restartAnimation = useCallback(() => {
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    const q = gsap.utils.selector(container.current);

    timelineRef.current = gsap
      .timeline({
        repeat: -1,
        // Built paused; played below only if already in view, otherwise the
        // offscreen observer starts it on entry.
        paused: true,
        onRepeat: () => {
          setActiveButton((current) => (current === 3 ? 0 : current + 1));
        },
      })
      .set(q('.svg-draw'), { drawSVG: '0%' })
      // Outline behind 4 boxes
      .fromTo(
        `#vertical-line`,
        { drawSVG: '0% 0%' },
        { duration: 0.2, drawSVG: '0% 20%', ease: 'none', delay: 1 },
      )
      .to(
        `#vertical-line`,
        { drawSVG: '80% 100%', duration: 0.4, ease: 'none' },
        '>',
      )
      .to(
        `#vertical-line`,
        { drawSVG: '100% 100%', duration: 0.2, ease: 'none' },
        '>',
      )
      .addLabel('outline', '<-0.3')
      .fromTo(
        [`#outline-top-motion`],
        { drawSVG: '47.5% 47.5%' },
        { duration: 0.3, drawSVG: `40% 47.5%`, ease: 'none' },
        'outline',
      )
      .to(
        [`#outline-top-motion`],
        { drawSVG: `27% 34.5%`, duration: 0.4, ease: 'none' },
        '>',
      )
      .to(
        [`#outline-top-motion`],
        { drawSVG: `15% 22.5%`, duration: 0.4, ease: 'none', delay: 0.5 },
        '>',
      )
      .to(
        [`#outline-top-motion`],
        { drawSVG: `2% 9.5%`, duration: 0.4, ease: 'none', delay: 0.7 },
        '>',
      )
      .to(
        [`#outline-top-motion`],
        { drawSVG: `2% 2%`, duration: 0.4, ease: 'none' },
        '>',
      )

      .fromTo(
        `#outline-bottom-motion`,
        { drawSVG: '2% 2%' },
        { duration: 0.3, drawSVG: `2% 9.5%`, ease: 'none' },
        'outline',
      )
      .to(
        [`#outline-bottom-motion`],
        { drawSVG: `15% 22.5%`, duration: 0.4, ease: 'none' },
        '>',
      )
      .to(
        [`#outline-bottom-motion`],
        { drawSVG: `27% 34.5%`, duration: 0.4, ease: 'none', delay: 0.5 },
        '>',
      )
      .to(
        `#outline-bottom-motion`,
        { drawSVG: `40% 48.5%`, duration: 0.6, delay: 0.5, ease: 'none' },
        '>',
      )
      .to(
        `#outline-bottom-motion`,
        { drawSVG: `48.5% 48.5%`, duration: 0.3, ease: 'none' },
        '>',
      );

    // //  card borders above outline
    addBorderAnimation(
      timelineRef.current,
      'cardBorder1',
      'box1',
      {
        ...iconStackDurations,
        tlPosition: 'outline+=0.5',
        startPosition: 'top',
        fadeDuration: 0.1,
        drawDuration: 0.7,
      },
      q,
    );
    addBorderAnimation(
      timelineRef.current,
      'cardBorder2',
      'box1',
      {
        ...iconStackDurations,
        tlPosition: 'outline+=0.5',
        startPosition: 'top',
        fadeDuration: 0.1,
        drawDuration: 0.7,
      },
      q,
    );
    addBorderAnimation(
      timelineRef.current,
      'cardBorder3',
      'box1',
      {
        ...iconStackDurations,
        tlPosition: 'outline+=1.5',
        startPosition: 'top',
        fadeDuration: 0.1,
        drawDuration: 0.7,
      },
      q,
    );
    addBorderAnimation(
      timelineRef.current,
      'cardBorder4',
      'box1',
      {
        ...iconStackDurations,
        tlPosition: 'outline+=1.5',
        startPosition: 'top',
        fadeDuration: 0.1,
        drawDuration: 0.7,
      },
      q,
    );

    timelineRef.current
      .fromTo(
        `#vertical-line-btm`,
        { drawSVG: '0% 0%' },
        { duration: 0.07, drawSVG: '0% 50%', ease: 'none' },
        'outline+=2.8',
      )
      .to(
        `#vertical-line-btm`,
        { drawSVG: '50% 100%', duration: 0.15, ease: 'none' },
        '>',
      )
      .to(
        `#vertical-line-btm`,
        { drawSVG: '100% 100%', duration: 0.07, ease: 'none' },
        '>',
      )
      .addLabel('finalBtns', 'outline+=3.2');
    addBorderAnimation(
      timelineRef.current,
      'initiativesBlock',
      'btns',
      {
        ...iconStackDurations,
        tlPosition: 'finalBtns',
        finalFadeDuration: 0.2,
        drawDuration: 0.5,
        fadeDuration: 0.2,
        startPosition: { topStart: 65, topEnd: 119 },
        linePercentage: 50,
      },
      q,
    );

    timelineRef.current.timeScale(LINE_DRAW_SPEED);
    if (isVisibleRef.current) timelineRef.current.play();
  }, [isVisibleRef]);

  // Robust resize listener with threshold and debouncing
  useEffect(() => {
    if (!container.current) return;

    let lastWidth = container.current.offsetWidth;
    let timeoutId: NodeJS.Timeout;

    const disconnect = observeElementResize(container.current, (entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        const widthDifference = Math.abs(width - lastWidth);

        // Only restart if width changed by more than 10px
        if (widthDifference > 10) {
          lastWidth = width;

          // Debounce to prevent multiple rapid fires
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            restartAnimation();
          }, 100);
        }
      }
    });

    return () => {
      disconnect();
      clearTimeout(timeoutId);
    };
  }, [restartAnimation]);

  useGSAP(
    () => {
      restartAnimation();

      return () => {
        timelineRef.current?.kill();
      };
    },
    { scope: container },
  );
  return (
    <div
      ref={container}
      className="flex flex-col items-center justify-center w-full max-w-[390px] mx-auto"
    >
      <AvatarBlock activeButton={activeButton} />
      <div className="flex flex-col items-center w-full relative">
        <div className="flex justify-center items-center absolute top-27">
          <div className="bg-charcoal rounded-full p-15 max-w-max max-h-max z-2 ">
            <TranscendLogo className="w-55" iconOnly />
          </div>
        </div>

        <div className="flex justify-center h-[70px] w-[2px]">
          <svg
            viewBox="0 0 2 70"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d="M0.927368 0.13623L0.927367 150"
              stroke={COLORS.grayscale['03']}
            />
            <path
              d="M0.927368 0.13623L0.927367 150"
              className="stroke-electric-04 svg-draw"
              id="vertical-line"
              strokeWidth={2}
            />
          </svg>
        </div>

        <div
          className="py-80 relative w-full
      "
        >
          <div className="absolute top-0 left-[25%] w-[50%] h-full">
            <div className="relative rounded-60 w-full h-full ">
              <AnimatedBorderGenerator
                topMotionId="outline-top-motion"
                bottomMotionId="outline-bottom-motion"
                borderRadius={60}
                strokeWidth={1}
                strokeColor={COLORS.grayscale['03']}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-y-30 gap-x-auto place-items-center">
            <CardIconBlock {...cardIconBlocks.ai} />
            <CardIconBlock
              {...cardIconBlocks.dataWarehouses}
              iconBlockId="cardBorder2"
              text={
                <>
                  Data <br />
                  warehouses
                </>
              }
            />

            <CardIconBlock {...cardIconBlocks.marketingAdTech} />
            <CardIconBlock
              {...cardIconBlocks.engagementPlatforms}
              iconBlockId="cardBorder4"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-center h-[60px] w-[2px]">
        <svg
          viewBox="0 0 2 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0.927368 0.13623L0.927367 60"
            stroke={COLORS.grayscale['03']}
          />
          <path
            d="M0.927368 0.13623L0.927367 60"
            className="stroke-electric-04 svg-draw"
            id="vertical-line-btm"
            strokeWidth={1.5}
          />
        </svg>
      </div>
      <InitiativesBlock iconBlockId="initiativesBlock" />
    </div>
  );
}
